import { slicedToArray as _slicedToArray } from '../../_virtual/_rollupPluginBabelHelpers.js';
import { Box, Button, IconArrowRight } from '@zillow/constellation';
import { useYieldCallback } from '@zillow/yield-callback';
import PropTypes from 'prop-types';
import React from 'react';
import { isBrowser } from '../../utils/utilities.js';
import OverlappingStack from '../PAFormStepper/OverlappingStack.js';
import { ListItemWithProgress, ImageWithProgress } from './ProgressTracker.js';

var _MediaStepper = function MediaStepper(_ref) {
  var animationTime = _ref.animationTime,
    buttonText = _ref.buttonText,
    children = _ref.children,
    collapseOthers = _ref.collapseOthers,
    onButtonClick = _ref.onButtonClick,
    reverse = _ref.reverse,
    onStepClick = _ref.onStepClick;
  var _React$useState = React.useState(false),
    _React$useState2 = _slicedToArray(_React$useState, 2),
    skipAnimation = _React$useState2[0],
    setSkipAnimation = _React$useState2[1];
  var _React$useState3 = React.useState(0),
    _React$useState4 = _slicedToArray(_React$useState3, 2),
    activeNumber = _React$useState4[0],
    setActiveNumber = _React$useState4[1];
  var intervalId = React.useRef(undefined);
  var _React$useState5 = React.useState(false),
    _React$useState6 = _slicedToArray(_React$useState5, 2),
    hashNavigated = _React$useState6[0],
    setHashNavigated = _React$useState6[1];
  var scrollToIdRef = React.useRef(null);

  // Clear interval helper function to avoid code duplication
  var clearCurrentInterval = React.useCallback(function () {
    if (intervalId.current) {
      clearInterval(intervalId.current);
      intervalId.current = undefined;
    }
  }, []);
  var onPropClick = useYieldCallback(function (propNumber, div) {
    setSkipAnimation(true);
    setActiveNumber(propNumber);
    clearCurrentInterval();
    onStepClick === null || onStepClick === void 0 || onStepClick(div);
  }, [clearCurrentInterval, onStepClick]);

  // Filter out children that are not wrapped in MediaStepper.Item
  // Memoize items to avoid referential changes on parent re-renders
  var items = React.useMemo(function () {
    return React.Children.toArray(children).filter(function (child) {
      return child.type === _MediaStepper.Item;
    });
  }, [children]);

  // Function to handle hash navigation
  var handleHashNavigation = React.useCallback(function () {
    if (!isBrowser) {
      return;
    }
    var hash = window.location.hash;
    if (!hash) {
      return;
    }

    // Remove the # from the hash
    var targetId = hash.substring(1);

    // Find the index of the item with matching id
    var itemIndex = items.findIndex(function (item) {
      return item.props.id && item.props.id === targetId;
    });
    if (itemIndex === -1) {
      return;
    }

    // Set the active item to the one matching the hash
    setActiveNumber(itemIndex);
    setSkipAnimation(true);
    setHashNavigated(true);

    // Store the target ID to scroll to in the next layout effect
    scrollToIdRef.current = targetId;

    // Clear any existing interval
    clearCurrentInterval();
  }, [items, clearCurrentInterval]);

  // Handle initial URL hash navigation
  React.useEffect(function () {
    handleHashNavigation();
  }, [handleHashNavigation]); // Run when handleHashNavigation changes

  // Use useLayoutEffect to handle scrolling after the DOM is updated
  React.useLayoutEffect(function () {
    if (!scrollToIdRef.current) {
      return;
    }
    var targetId = scrollToIdRef.current;
    var element = document.getElementById(targetId);
    if (element) {
      element.scrollIntoView({
        behavior: 'smooth',
        block: 'start'
      });

      // Ensure the element is marked as active by finding its index
      var itemIndex = items.findIndex(function (item) {
        return item.props.id === targetId;
      });
      if (itemIndex === -1) {
        return;
      }

      // Force a re-render to ensure active state is applied
      setActiveNumber(itemIndex);
    }

    // Clear the ref after handling
    scrollToIdRef.current = null;
  }, [items]);

  // Add event listener for hash changes
  React.useEffect(function () {
    if (!isBrowser) {
      return;
    }

    // Add event listener
    window.addEventListener('hashchange', handleHashNavigation);

    // Clean up
    return function () {
      window.removeEventListener('hashchange', handleHashNavigation);
    };
  }, [handleHashNavigation]); // Re-add listener if handleHashNavigation changes

  // Set up interval for auto-advancing steps
  React.useEffect(function () {
    // Only set up the interval if hash navigation wasn't used
    if (!animationTime || animationTime < 0 || hashNavigated) {
      return;
    }

    // Clear any existing interval first to prevent memory leaks
    clearCurrentInterval();
    intervalId.current = setInterval(function () {
      setActiveNumber(function (prevActiveNumber) {
        return (prevActiveNumber + 1) % items.length;
      });
    }, 1000 * animationTime);

    // Clean up on unmount or when dependencies change
    return clearCurrentInterval;
  }, [animationTime, hashNavigated, items.length, clearCurrentInterval]);
  var onButtonClickYielded = useYieldCallback(function () {
    onButtonClick === null || onButtonClick === void 0 || onButtonClick();
  }, [onButtonClick]);

  // Memoize item rendering functions to prevent unnecessary re-renders
  var renderListItems = React.useCallback(function (item, index) {
    var active = index === activeNumber;
    return /*#__PURE__*/React.createElement(React.Fragment, {
      key: index
    }, /*#__PURE__*/React.createElement(ListItemWithProgress, {
      heading: item.props.heading,
      active: active,
      animationTime: animationTime,
      skipAnimation: skipAnimation,
      onClick: function onClick(div) {
        onPropClick(index, div);
      },
      collapseOthers: collapseOthers,
      id: item.props.id
    }, item.props.children));
  }, [activeNumber, animationTime, skipAnimation, onPropClick, collapseOthers]);
  var renderImageItems = React.useCallback(function (item, index) {
    return /*#__PURE__*/React.createElement(ImageWithProgress, {
      key: index,
      active: activeNumber === index,
      media: item.props.image
    });
  }, [activeNumber]);
  return /*#__PURE__*/React.createElement("div", null, /*#__PURE__*/React.createElement(Box, {
    sx: {
      gap: '$spacing.sm',
      display: 'grid',
      gridTemplateColumns: '1fr',
      '@media $lg_gte': {
        gap: '$spacing.md',
        gridTemplateColumns: '1fr 1fr'
      }
    }
  }, /*#__PURE__*/React.createElement(Box, {
    sx: Object.assign({}, reverse && {
      order: 1
    }, {
      display: 'flex',
      gap: '$spacing.lg',
      'flex-direction': 'column',
      'margin-right': '$spacing.none',
      '@media $xl_gte': {
        'margin-right': '15%'
      }
    })
  }, items.map(renderListItems), !!buttonText && /*#__PURE__*/React.createElement(Box, {
    as: Button,
    sx: {
      marginTop: '$spacing.lg !important',
      alignSelf: 'flex-start'
    },
    buttonType: "primary",
    iconPosition: "end",
    icon: /*#__PURE__*/React.createElement(IconArrowRight, null),
    onClick: onButtonClickYielded
  }, buttonText)), /*#__PURE__*/React.createElement(OverlappingStack, {
    sx: Object.assign({}, !reverse && {
      justifyContent: 'end'
    }, {
      '@media $md_lte': {
        display: 'none'
      }
    })
  }, items.map(renderImageItems))));
};

// Safe to disable as I define displayName on next line.
// eslint-disable-next-line react/display-name
_MediaStepper.Item = function () {
  return null;
};
_MediaStepper.Item.displayName = 'MediaStepper.Item';
_MediaStepper.Item.propTypes = {
  /** Child content to be displayed for this step */
  children: PropTypes.node,
  /** Heading text for this step */
  heading: PropTypes.string.isRequired,
  /** Image to be displayed for this step */
  image: PropTypes.shape({
    /** Alt text for the image */
    alt: PropTypes.string.isRequired,
    /** Image source URL */
    src: PropTypes.string.isRequired,
    /** Image width in pixels */
    width: PropTypes.number.isRequired,
    /** Image height in pixels */
    height: PropTypes.number.isRequired
  }).isRequired,
  /** Optional ID for hash navigation and accessibility */
  id: PropTypes.string
};
_MediaStepper.defaultProps = {
  animationTime: 6,
  collapseOthers: false
};
_MediaStepper.propTypes = process.env.NODE_ENV !== "production" ? {
  /** Time in seconds between auto-advancing steps (set to 0 or negative to disable) */
  animationTime: PropTypes.number,
  /** Text for the call to action button (if not provided, no button will be shown) */
  buttonText: PropTypes.string,
  /** MediaStepper.Item components representing each step */
  children: PropTypes.node.isRequired,
  /** Whether to collapse other items when one is expanded */
  collapseOthers: PropTypes.bool.isRequired,
  /** Required heading for the component */
  heading: PropTypes.string.isRequired,
  /** Handler called when the button is clicked */
  onButtonClick: PropTypes.func,
  /** Whether to reverse the layout (content on right, images on left) */
  reverse: PropTypes.bool,
  /** Handler called when a step is clicked */
  onStepClick: PropTypes.func
} : {};

/**
 * MediaStepper is a component that displays a series of steps with associated media.
 * It supports auto-advancing through steps, hash navigation, and responsive layouts.
 */
var _MediaStepper$1 = _MediaStepper;

export { _MediaStepper$1 as default };
